import { LogOut, Settings, Sparkles } from 'lucide-react';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';

import { MAIN_TABS, orderAndFilterMemberTabs, SUB_TABS } from './memberNavigation';

import { ThemeToggleExpanded } from '@/components/shared/ThemeToggle';

/**
 * Mobile bottom sheet navigation
 * Contains main tabs, sub tabs, and sign out button
 */
const MobileNavSheet = ({
  open,
  onOpenChange,
  activeTab,
  onTabChange,
  subSection,
  onSubSectionChange,
  canViewBilling = true,
  contentRestricted = false,
  isSectionVisible = () => true,
  isLoaded = true,
  onLogout,
  navigationPreferences = {},
  onOpenPreferences = () => {},
  onOpenWhatsNew = () => {},
  hasUnreadUpdates = false,
  onAppearanceChange = () => {},
  appearanceSaving = false,
}) => {
  const hasSubTabs = SUB_TABS[activeTab];
  const allSubTabs = hasSubTabs ? SUB_TABS[activeTab] : [];

  // Filter tabs based on permissions, content access, and portal section visibility
  const accessibleTabs = MAIN_TABS.filter((tab) => {
    if (tab.id === 'billing') return canViewBilling;
    if (tab.id === 'resources' || tab.id === 'learning') return !contentRestricted;
    return true;
  }).filter((tab) => isSectionVisible(tab.id));
  const visibleTabs = orderAndFilterMemberTabs(accessibleTabs, navigationPreferences);

  // Filter sub-tabs by portal section visibility
  const currentSubTabs = allSubTabs.filter((sub) =>
    isSectionVisible(`${activeTab}.${sub.id}`)
  );

  const handleTabClick = (tabId) => {
    onTabChange(tabId);
    // Don't close sheet when changing tabs, let user also select sub-tab
  };

  const handleSubSectionClick = (sectionId) => {
    onSubSectionChange(sectionId);
    onOpenChange(false); // Close sheet after selecting sub-section
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="mobile-nav-sheet bg-white dark:bg-[#1A1814] text-stone-800 dark:text-white p-0 rounded-t-3xl max-h-[85vh] overflow-y-auto border-t-2 border-[#C9A227]"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>Member navigation</SheetTitle>
          <SheetDescription>
            Choose a portal destination, update personal preferences, or sign out.
          </SheetDescription>
        </SheetHeader>

        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1 bg-[#C9A227] rounded-full" />
        </div>

        {/* Main Tabs */}
        <div className="px-4 pb-4">
          <p className="text-xs uppercase text-[#C9A227] font-semibold tracking-wider mb-3">
            Navigate
          </p>
          <div className="space-y-1">
            {!isLoaded
              ? MAIN_TABS.map((tab) => (
                  <div
                    key={tab.id}
                    className="w-full flex items-center gap-3 p-3 rounded-xl"
                    data-testid="mobile-nav-skeleton"
                  >
                    <Skeleton className="h-5 w-5 rounded-full" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                ))
              : visibleTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={`
                    w-full flex items-center gap-3 p-3 rounded-xl transition-all
                    ${isActive
                      ? 'bg-[#9B1B30] text-white'
                      : 'text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-white/10 active:bg-stone-200 dark:active:bg-white/20'
                    }
                  `}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Sub Tabs (if applicable) */}
        {hasSubTabs && currentSubTabs.length > 0 && (
          <div className="px-4 pb-4 border-t border-stone-200 dark:border-white/10 pt-4">
            <p className="text-xs uppercase text-[#C9A227] font-semibold tracking-wider mb-3">
              {activeTab === 'progress' && 'Progress Sections'}
              {activeTab === 'learning' && 'Learning Sections'}
              {activeTab === 'resources' && 'Resource Types'}
            </p>
            <div className="space-y-1">
              {currentSubTabs.map((section) => {
                const Icon = section.icon;
                const isActive = subSection === section.id;
                return (
                  <button
                    key={section.id}
                    onClick={() => handleSubSectionClick(section.id)}
                    className={`
                      w-full flex items-center gap-3 p-3 rounded-xl transition-all
                      ${isActive
                        ? 'bg-[#C9A227]/20 text-[#C9A227]'
                        : 'text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-white/5 active:bg-stone-200 dark:active:bg-white/10'
                      }
                    `}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-sm font-medium">{section.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Appearance */}
        <div className="px-4 pb-4 border-t border-stone-200 dark:border-white/10 pt-4">
          <div className="mb-3 grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              disabled={appearanceSaving}
              onClick={() => {
                onOpenChange(false);
                window.requestAnimationFrame(onOpenPreferences);
              }}
            >
              <Settings className="mr-2 h-4 w-4" /> Preferences
            </Button>
            <Button
              variant="outline"
              className="relative"
              onClick={() => {
                onOpenChange(false);
                window.requestAnimationFrame(onOpenWhatsNew);
              }}
            >
              <Sparkles className="mr-2 h-4 w-4" /> What&apos;s New
              {hasUnreadUpdates && <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-[#9B1B30]" aria-label="Unread updates" />}
            </Button>
          </div>
          <ThemeToggleExpanded
            onThemeChange={onAppearanceChange}
            disabled={appearanceSaving}
          />
        </div>

        {/* Sign Out */}
        <div className="px-4 pb-6 border-t border-stone-200 dark:border-white/10 pt-4">
          <Button
            variant="outline"
            className="w-full bg-transparent border-red-500/50 text-red-500 dark:text-red-400 hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-300"
            onClick={() => {
              onLogout();
              onOpenChange(false);
            }}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileNavSheet;
